
import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks/use-debounce';
import { LocationCard } from '@/components/locations/LocationCard';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { ItineraryListSkeleton } from '@/components/itinerary/dashboard/ItineraryListSkeleton';
import type { Loch } from '@/app/lib/types';


async function fetchLochs(searchTerm: string): Promise<Loch[]> {
    const API_BASE_URL = 'https://ai-travel-itinerary-ai-228543204937.europe-west2.run.app/api';
    let response;

    try {
        if (!searchTerm) {
            response = await fetch(`${API_BASE_URL}/get_lochs`);
        } else {
            const body = {
                filters: [{
                    field: "Loch Name",
                    operator: "contains",
                    value: searchTerm
                }],
                sortBy: {
                    type: "field",
                    field: "Loch Name",
                    direction: "asc"
                },
                pagination: {
                    limit: 100,
                    offset: 0
                }
            };
            response = await fetch(`${API_BASE_URL}/search_lochs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
        }

        if (!response.ok) {
            const errorData = await response.json();
            console.error('Lochs API Error:', errorData);
            throw new Error(errorData?.error?.message || `Failed to fetch lochs: ${response.statusText}`);
        }
        
        let data = await response.json();
        
        // Handle both direct array (from get_lochs) and object with 'results' property (from search_lochs).
        // Also handle the case where 'results' is null.
        const results = Array.isArray(data) ? data : (data.results || []);

        if (!Array.isArray(results)) {
            console.error("API did not return a valid array of lochs:", results);
            throw new Error("Invalid data format received from the server.");
        }

        // The API returns minimal data, so we need to supplement it for the UI.
        const supplementedData = results.map((loch: any) => ({
          ...loch,
          slug: loch.id,
          name: loch['Loch Name'],
          region: loch['County/Region'],
        }));
        
        return supplementedData as Loch[];

    } catch (error) {
        console.error("fetchLochs failed:", error);
        throw error;
    }
}


export default function LocationsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [lochs, setLochs] = useState<Loch[]>([]);
  const [isSearching, setIsSearching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    setIsSearching(true);
    setError(null);

    fetchLochs(debouncedSearchTerm)
      .then(data => {
        setLochs(data);
      })
      .catch(err => {
        setError(err.message || 'An unexpected error occurred.');
        setLochs([]);
      })
      .finally(() => {
        setIsSearching(false);
      });
  }, [debouncedSearchTerm]);


  return (
    <div className="container mx-auto">
      <div className="mb-4 sm:mb-6 md:mb-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">Explore Swim Spots</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">Discover the best lochs, rivers, and coastlines for a refreshing dip in Scotland.</p>
        <div className="relative mt-3 sm:mt-4 md:mt-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 sm:h-5 w-4 sm:w-5 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search by name or region..."
            className="w-full pl-9 sm:pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      {isSearching && <ItineraryListSkeleton />}

      {!isSearching && error && (
         <div className="text-center py-16 border-2 border-dashed rounded-lg border-destructive/50 bg-destructive/10">
            <h2 className="text-xl font-semibold text-destructive">Failed to Load Swim Spots</h2>
            <p className="text-muted-foreground mt-2">{error}</p>
        </div>
      )}

      {!isSearching && !error && lochs.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {lochs.map((location) => (
            <LocationCard key={location.id} location={location} />
          ))}
        </div>
      ) : (
        !isSearching && !error && (
            <div className="text-center py-16 border-2 border-dashed rounded-lg">
                <h2 className="text-xl font-semibold">No Swim Spots Found</h2>
                <p className="text-muted-foreground mt-2">Try adjusting your search term.</p>
            </div>
        )
      )}
    </div>
  );
}
